import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CalendarClock,
    PackageSearch,
    PackageCheck,
    ArrowRight,
    MapPin,
    Receipt,
    Sparkles,
    BadgeCheck,
    Star,
    PencilLine,
    Hourglass,
    CreditCard,
    XCircle,
    Gavel,
    MessageCircle,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PickupActions } from "../components/PickupActions";
import PayoutRequiredDialog, { detectPayoutRequired } from "../components/PayoutRequiredDialog";
import useDeepLinkedRental from "../hooks/useDeepLinkedRental";
import { MY_RENTALS, RENTAL_REVIEW, RETURN_RENTAL, DISPUTE } from "../lib/testIds";
import {
    fetchMyRentals,
    createCheckoutSession,
    cancelRentalRequest,
} from "../lib/api";
import ReviewModal from "../components/ReviewModal";
import ReturnRentalModal from "../components/ReturnRentalModal";
import DisputeThreadModal from "../components/DisputeThreadModal";
import AskQuestionModal from "../components/AskQuestionModal";
import NextStepDialog from "../components/NextStepDialog";

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

const STATUS_BADGE = {
    active: "bg-[#737a3d]/15 text-[#737a3d]",
    upcoming: "bg-[#2e3220]/10 text-[#2e3220]/85",
    pending: "bg-[#d6a73f]/15 text-[#7a5c1a]",
    awaiting_payment: "bg-[#B56C5A]/15 text-[#7a3a30]",
    return_pending: "bg-[#d6a73f]/15 text-[#7a5c1a]",
    declined: "bg-[#2e3220]/10 text-[#2e3220]/60",
    cancelled: "bg-[#2e3220]/10 text-[#2e3220]/60",
    completed: "bg-[#5a6142]/20 text-[#5a6142]",
};

const StarRowDisplay = ({ value }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star
                key={n}
                className={`h-3.5 w-3.5 ${
                    n <= (value || 0)
                        ? "fill-[#d6a73f] text-[#d6a73f]"
                        : "text-[#2e3220]/20"
                }`}
                strokeWidth={2}
            />
        ))}
    </div>
);

const BUCKET_LABELS = {
    pending: "Awaiting response",
    awaiting_payment: "Ready to pay",
    active: "Active",
    return_pending: "Return sent",
    upcoming: "Upcoming",
    completed: "Completed",
    declined: "Declined",
    cancelled: "Cancelled",
};

const RentalRow = ({ rental, bucket, onReview, onPay, onCancel, onReturn, onDispute, onAsk, busyId }) => {
    const badgeClass = STATUS_BADGE[bucket] || STATUS_BADGE.active;
    const canReturn = rental.status === "active";
    const canReview = bucket === "completed" && !canReturn;
    const review = rental.review;
    const isPaid = ["active", "upcoming", "completed"].includes(bucket);
    const isAwaitingPayment = bucket === "awaiting_payment";
    const isPending = bucket === "pending";
    const isFinal = bucket === "declined" || bucket === "cancelled";
    const busy = busyId === rental.id;
    return (
        <li
            data-testid={MY_RENTALS.row(rental.id)}
            data-rental-id={rental.id}
            className={`overflow-hidden rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] transition-all duration-300 hover:shadow-[0_10px_30px_rgba(46,50,32,0.08)] ${isFinal ? "opacity-70" : ""}`}
        >
            <div className="grid gap-0 md:grid-cols-[160px_1fr_auto]">
                <div className="relative aspect-[4/3] md:aspect-auto md:h-full bg-[#E8E2D0]">
                    {rental.item_image && (
                        <img
                            src={rental.item_image}
                            alt={rental.item_name}
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                        />
                    )}
                </div>
                <div className="flex flex-col gap-3 px-5 py-5">
                    <div className="flex flex-wrap items-center gap-2">
                        <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${badgeClass}`}
                        >
                            {isPending && <Hourglass className="h-3 w-3" strokeWidth={2.5} />}
                            {isAwaitingPayment && <CreditCard className="h-3 w-3" strokeWidth={2.5} />}
                            {BUCKET_LABELS[bucket] || bucket}
                        </span>
                        {isPaid && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#737a3d]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
                                Paid via Stripe
                            </span>
                        )}
                    </div>
                    <h3 className="font-display text-xl font-bold leading-tight text-[#2e3220]">
                        {rental.item_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[#5a6142]">
                        <span className="inline-flex items-center gap-1">
                            <CalendarClock className="h-3.5 w-3.5" />
                            {formatDate(rental.start_date)} → {formatDate(rental.end_date)}
                        </span>
                        {rental.owner_name && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                Lent by{" "}
                                {rental.owner_id ? (
                                    <Link
                                        to={`/neighbors/${rental.owner_id}`}
                                        className="font-semibold text-[#737a3d] hover:text-[#2e3220] hover:underline"
                                    >
                                        {rental.owner_name}
                                    </Link>
                                ) : (
                                    rental.owner_name
                                )}
                            </span>
                        )}
                    </div>

                    {(rental.status === "active" || rental.status === "return_pending") &&
                        (rental.item_location || rental.item_pickup_instructions || rental.item_full_address) && (
                            <div className="mt-1 rounded-2xl bg-[#737a3d]/8 border border-[#737a3d]/20 px-3 py-2">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    Pickup details
                                </p>
                                {rental.item_full_address ? (
                                    <p className="mt-1 inline-flex items-start gap-1 text-xs font-semibold text-[#2e3220]">
                                        <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                                        {rental.item_full_address}
                                    </p>
                                ) : rental.item_location ? (
                                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-[#2e3220]">
                                        <MapPin className="h-3 w-3" />
                                        {rental.item_location}
                                    </p>
                                ) : null}
                                {rental.item_pickup_instructions && (
                                    <p className="mt-1 text-xs leading-relaxed text-[#5a6142]">
                                        {rental.item_pickup_instructions}
                                    </p>
                                )}
                                <PickupActions
                                    address={rental.item_full_address}
                                    testIdPrefix={`my-rentals-pickup-${rental.id}`}
                                />
                            </div>
                        )}

                    {canReturn && (
                        <div className="mt-1">
                            <button
                                type="button"
                                data-testid={RETURN_RENTAL.cta(rental.id)}
                                disabled={busy}
                                onClick={() => onReturn?.(rental)}
                                className="inline-flex items-center gap-1.5 self-start rounded-full bg-[#737a3d] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] transition-all duration-200 hover:bg-[#5f6534] disabled:opacity-50"
                            >
                                {busy ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <PackageCheck className="h-3 w-3" strokeWidth={2.5} />
                                )}
                                Return item
                            </button>
                        </div>
                    )}

                    {/* Ask a Question — available whenever the rental is active with a counterparty */}
                    {(bucket === "active" ||
                        bucket === "upcoming" ||
                        bucket === "return_pending" ||
                        bucket === "awaiting_payment") &&
                        onAsk && (
                            <div className="mt-1">
                                <button
                                    type="button"
                                    data-testid={`rental-ask-question-${rental.id}`}
                                    onClick={() => onAsk(rental)}
                                    className="inline-flex items-center gap-1.5 self-start rounded-full border border-[#737a3d]/40 bg-transparent px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#737a3d] transition-all duration-200 hover:bg-[#737a3d]/5"
                                >
                                    <MessageCircle className="h-3 w-3" strokeWidth={2.5} />
                                    Ask {rental.owner_name?.split(" ")[0] || "lender"} a question
                                </button>
                            </div>
                        )}

                    {canReview && (
                        <div className="mt-1">
                            {review ? (
                                <div
                                    data-testid={RENTAL_REVIEW.display(rental.id)}
                                    className="flex flex-wrap items-center gap-3 rounded-2xl bg-[#2e3220]/5 px-3 py-2"
                                >
                                    <StarRowDisplay value={review.rating} />
                                    {review.comment && (
                                        <p className="min-w-0 flex-1 truncate text-xs text-[#5a6142]">
                                            “{review.comment}”
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={RENTAL_REVIEW.edit(rental.id)}
                                        onClick={() => onReview(rental)}
                                        className="inline-flex items-center gap-1 rounded-full bg-[#F4F1EA] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] hover:text-[#2e3220]"
                                    >
                                        <PencilLine className="h-3 w-3" strokeWidth={2.5} />
                                        Edit
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    data-testid={RENTAL_REVIEW.cta(rental.id)}
                                    onClick={() => onReview(rental)}
                                    className="inline-flex items-center gap-1.5 self-start rounded-full bg-[#2e3220] px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] transition-all duration-200 hover:bg-[#3d4230]"
                                >
                                    <Star className="h-3.5 w-3.5" strokeWidth={2.5} />
                                    Leave a review
                                </button>
                            )}
                        </div>
                    )}

                    {(bucket === "completed" || bucket === "return_pending") && (rental.return_photos?.length > 0 || rental.condition_note) && (
                        <div
                            data-testid={RETURN_RENTAL.afterGallery(rental.id)}
                            className="mt-1 rounded-2xl bg-[#2e3220]/5 px-3 py-2"
                        >
                            <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                {bucket === "return_pending" ? "Return sent · waiting on lender" : "Return record"}
                            </p>
                            {rental.condition_note && (
                                <p className="mt-1 text-xs italic text-[#5a6142]">
                                    “{rental.condition_note}”
                                </p>
                            )}
                            {rental.return_photos?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {rental.return_photos.map((url, i) => (
                                        <a
                                            key={`${rental.id}-rp-${i}`}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block h-12 w-12 overflow-hidden rounded-lg border border-[#2e3220]/10 bg-[#E8E2D0] transition-transform hover:scale-105"
                                        >
                                            <img
                                                src={url}
                                                alt={`Return ${i + 1}`}
                                                className="h-full w-full object-cover"
                                                loading="lazy"
                                            />
                                        </a>
                                    ))}
                                </div>
                            )}
                            {/* Deposit + dispute badges are hidden per
                                product decision. Backend data (deposit_status,
                                dispute_status) is preserved on the rental
                                so this UI can be flipped back on later. */}
                            {false && rental.deposit_status === "released" && (
                                <p className="mt-2 inline-block rounded-full bg-[#737a3d]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#5f6534]">
                                    Deposit released
                                    {rental.deposit_refund_id && " · Stripe refund issued"}
                                </p>
                            )}
                            {false && rental.deposit_status === "withheld" && (
                                <div className="mt-2">
                                    <span className="inline-block rounded-full bg-[#B56C5A]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#7a3a30]">
                                        Deposit withheld
                                    </span>
                                    {rental.deposit_withheld_reason && (
                                        <p className="mt-1 rounded-2xl bg-[#B56C5A]/10 px-3 py-2 text-xs italic text-[#7a3a30]">
                                            Reason: “{rental.deposit_withheld_reason}”
                                        </p>
                                    )}
                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                        {rental.dispute_status ? (
                                            <button
                                                type="button"
                                                data-testid={DISPUTE.viewBtn(rental.id)}
                                                onClick={() => onDispute?.(rental)}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-[#2e3220] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#3d4230]"
                                            >
                                                <Gavel className="h-3 w-3" strokeWidth={2.5} />
                                                View dispute
                                                <span
                                                    data-testid={DISPUTE.badge(rental.id)}
                                                    className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] ${
                                                        rental.dispute_status === "open"
                                                            ? "bg-[#B56C5A] text-[#F4F1EA]"
                                                            : "bg-[#737a3d] text-[#F4F1EA]"
                                                    }`}
                                                >
                                                    {rental.dispute_status}
                                                </span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid={DISPUTE.openBtn(rental.id)}
                                                onClick={() => onDispute?.(rental)}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-[#B56C5A] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#9d5b4c]"
                                            >
                                                <Gavel className="h-3 w-3" strokeWidth={2.5} />
                                                Dispute this
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {isAwaitingPayment && (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                data-testid={`rental-pay-${rental.id}`}
                                disabled={busy}
                                onClick={() => onPay(rental)}
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#737a3d] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#5f6534] disabled:opacity-50"
                            >
                                {busy ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <CreditCard className="h-3 w-3" strokeWidth={2.5} />
                                )}
                                Complete payment
                            </button>
                            <button
                                type="button"
                                data-testid={`rental-cancel-${rental.id}`}
                                disabled={busy}
                                onClick={() => onCancel(rental)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#2e3220]/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#5a6142] hover:bg-[#2e3220]/5 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                    {isPending && (
                        <div className="mt-1">
                            <button
                                type="button"
                                data-testid={`rental-cancel-${rental.id}`}
                                disabled={busy}
                                onClick={() => onCancel(rental)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#2e3220]/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#5a6142] hover:bg-[#2e3220]/5 disabled:opacity-50"
                            >
                                <XCircle className="h-3 w-3" strokeWidth={2.5} />
                                Cancel request
                            </button>
                        </div>
                    )}
                </div>
                <div className="flex flex-row items-center justify-between gap-4 border-t border-[#2e3220]/10 px-5 py-4 md:flex-col md:items-end md:justify-center md:border-l md:border-t-0 md:py-5">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Paid
                        </p>
                        <p className="font-display text-2xl font-bold text-[#2e3220]">
                            {formatMoney(rental.total_amount)}
                        </p>
                        <p className="text-[10px] text-[#5a6142]">
                            {rental.days} day{rental.days > 1 ? "s" : ""} · {formatMoney(rental.price_per_day)}/day
                        </p>
                    </div>
                </div>
            </div>
        </li>
    );
};

const Section = ({ title, bucket, items, testId, onReview, onPay, onCancel, onReturn, onDispute, onAsk, busyId }) => {
    if (!items?.length) return null;
    return (
        <section data-testid={testId} className="mt-10">
            <div className="mb-3 flex items-end justify-between">
                <h2 className="font-display text-xl font-bold text-[#2e3220]">
                    {title}
                </h2>
                <p className="text-xs text-[#5a6142]">
                    {items.length} rental{items.length === 1 ? "" : "s"}
                </p>
            </div>
            <ul className="space-y-4">
                {items.map((r) => (
                    <RentalRow
                        key={r.id}
                        rental={r}
                        bucket={bucket}
                        onReview={onReview}
                        onPay={onPay}
                        onCancel={onCancel}
                        onReturn={onReturn}
                        onDispute={onDispute}
                        onAsk={onAsk}
                        busyId={busyId}
                    />
                ))}
            </ul>
        </section>
    );
};

const KpiCard = ({ label, value, sub, testId, accent }) => (
    <div
        data-testid={testId}
        className={`rounded-3xl border border-[#2e3220]/10 p-6 ${
            accent ? "bg-[#2e3220] text-[#F4F1EA]" : "bg-[#F4F1EA]"
        }`}
    >
        <p
            className={`text-[10px] font-bold uppercase tracking-widest ${
                accent ? "text-[#8a9450]" : "text-[#737a3d]"
            }`}
        >
            {label}
        </p>
        <p
            className={`mt-3 font-display text-3xl font-bold tracking-tight ${
                accent ? "text-[#F4F1EA]" : "text-[#2e3220]"
            }`}
        >
            {value}
        </p>
        {sub && (
            <p
                className={`mt-1 text-xs ${
                    accent ? "text-[#F4F1EA]/70" : "text-[#5a6142]"
                }`}
            >
                {sub}
            </p>
        )}
    </div>
);

const MyRentals = () => {
    const { user } = useAuth();
    const [data, setData] = useState({ active: [], upcoming: [], past: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [reviewTarget, setReviewTarget] = useState(null);
    const [returnTarget, setReturnTarget] = useState(null);
    const [disputeTarget, setDisputeTarget] = useState(null);
    const [askTarget, setAskTarget] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchMyRentals()
            .then((d) => {
                if (cancelled) return;
                setData(
                    d || {
                        pending: [],
                        awaiting_payment: [],
                        active: [],
                        return_pending: [],
                        upcoming: [],
                        past: [],
                        declined: [],
                        total: 0,
                    },
                );
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleReviewSubmitted = (review) => {
        if (!reviewTarget) return;
        setData((prev) => ({
            ...prev,
            past: prev.past.map((r) =>
                r.id === reviewTarget.id ? { ...r, review } : r,
            ),
        }));
    };

    const [busyId, setBusyId] = useState(null);
    // Filter toggle: default to "active" (Currently Renting). Manual clicks
    // sticky.
    const [filter, setFilter] = useState("active");
    // Stripe Connect renter-side gate — auto-opens if the owner hasn't
    // finished payout onboarding when the renter clicks Pay now. Tracks
    // the target rental so we can pass item context into the dialog.
    const [payoutBlockedRental, setPayoutBlockedRental] = useState(null);
    // Big centered next-step confirmation modal — mirrors the "Request
    // sent!" panel so every action gives clear directional guidance.
    const [nextStep, setNextStep] = useState(null);

    // Deep-link support — if the user arrived via a notification/email
    // link with `?rental=<id>&tab=<name>`, switch the tab and scroll+glow
    // the specific rental card into view.
    useDeepLinkedRental({ setTab: setFilter, loading });

    const handlePay = async (rental) => {
        try {
            setBusyId(rental.id);
            const data = await createCheckoutSession({
                rentalId: rental.id,
                originUrl: window.location.origin,
            });
            if (!data?.url) throw new Error("Missing checkout URL");
            window.location.assign(data.url);
        } catch (err) {
            const gate = detectPayoutRequired(err);
            if (gate === "renter") {
                setPayoutBlockedRental(rental);
            } else {
                toast.error("Couldn't start checkout", {
                    description: err?.response?.data?.detail || "Please try again.",
                });
            }
            setBusyId(null);
        }
    };

    const handleCancel = async (rental) => {
        try {
            setBusyId(rental.id);
            await cancelRentalRequest(rental.id);
            toast.success("Request cancelled");
            setNextStep({
                title: "Request cancelled",
                description: `${rental.owner_name || "The lender"} has been notified and this rental is cleared from your Borrowing dashboard. You can browse other items or request the same one again with different dates any time.`,
            });
            // Refresh
            const fresh = await fetchMyRentals();
            setData(fresh);
        } catch (err) {
            toast.error("Couldn't cancel", {
                description: err?.response?.data?.detail || "Please try again.",
            });
        } finally {
            setBusyId(null);
        }
    };

    const handleReturn = (rental) => {
        setReturnTarget(rental);
    };

    const handleDispute = (rental) => {
        setDisputeTarget(rental);
    };

    const handleDisputeChanged = async () => {
        try {
            const fresh = await fetchMyRentals();
            setData(fresh);
        } catch {
            /* ignore */
        }
    };

    const handleReturned = async (returnedRental) => {
        // Return is now a 2-step flow — owner must accept before the rental
        // is completed and reviews unlock. Refresh the dashboard and show a
        // centered next-step modal explaining what happens next.
        try {
            const fresh = await fetchMyRentals();
            setData(fresh);
        } catch {
            /* ignore */
        }
        setNextStep({
            title: "Return submitted!",
            description: `${returnedRental?.owner_name || "Your lender"} has been notified and will review your return. Once they accept, this rental moves to 'Past rentals' and you'll be able to leave a review.`,
        });
    };

    const all = [
        ...(data.pending || []),
        ...(data.awaiting_payment || []),
        ...(data.active || []),
        ...(data.upcoming || []),
        ...(data.past || []),
    ];
    const spent = (data.past || []).reduce((s, r) => s + (Number(r.total_amount) || 0), 0);
    const hasAny = (data.total || 0) > 0;
    const pending = data.pending || [];
    const awaiting = data.awaiting_payment || [];

    return (
        <main
            data-testid={MY_RENTALS.root}
            className="relative z-10 mx-auto max-w-7xl px-6 py-14 md:px-10 md:py-20"
        >
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    Renter dashboard
                </span>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                        Borrowing
                    </h1>
                    <p className="mt-2 max-w-xl text-base leading-relaxed text-[#5a6142]">
                        Everything you&apos;re borrowing from the block — paid securely through
                        Stripe, with pickup arranged by your neighbor.
                    </p>
                </div>
                <Link
                    to="/browse"
                    data-testid={MY_RENTALS.browseBtn}
                    className="btn-primary"
                >
                    Browse more items
                    <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </Link>
            </div>

            <section className="mt-10 grid gap-2 sm:gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <button
                    type="button"
                    data-testid={MY_RENTALS.statActive}
                    onClick={() => setFilter("active")}
                    aria-pressed={filter === "active"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        filter === "active"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`text-[10px] font-bold uppercase tracking-widest ${filter === "active" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        Currently Borrowing
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : data.active.length}
                    </p>
                    <p
                        className={`mt-1 text-xs ${filter === "active" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {data.active.length
                            ? `${data.active.length} rental${data.active.length > 1 ? "s" : ""} in progress`
                            : "Nothing out right now"}
                    </p>
                </button>
                <button
                    type="button"
                    data-testid="my-rentals-stat-pending-payment"
                    onClick={() => setFilter("pending_payment")}
                    aria-pressed={filter === "pending_payment"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        filter === "pending_payment"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`text-[10px] font-bold uppercase tracking-widest ${filter === "pending_payment" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        Pending Payment
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : (data.awaiting_payment?.length || 0)}
                    </p>
                    <p
                        className={`mt-1 text-xs ${filter === "pending_payment" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {data.awaiting_payment?.length
                            ? "Owner accepted — pay to confirm"
                            : "Nothing waiting on you"}
                    </p>
                </button>
                <button
                    type="button"
                    data-testid="my-rentals-stat-return-pending"
                    onClick={() => setFilter("return_pending")}
                    aria-pressed={filter === "return_pending"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        filter === "return_pending"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`text-[10px] font-bold uppercase tracking-widest ${filter === "return_pending" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        Pending Return
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : (data.return_pending?.length || 0)}
                    </p>
                    <p
                        className={`mt-1 text-xs ${filter === "return_pending" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        {data.return_pending?.length
                            ? "Waiting for owner to accept"
                            : "Nothing awaiting return"}
                    </p>
                </button>
                <button
                    type="button"
                    data-testid="my-rentals-stat-past"
                    onClick={() => setFilter("past")}
                    aria-pressed={filter === "past"}
                    className={`rounded-2xl border p-3 sm:p-6 text-left transition-all duration-200 ${
                        filter === "past"
                            ? "border-[#737a3d] bg-[#737a3d] text-[#F4F1EA] shadow-lg shadow-[#737a3d]/20"
                            : "border-[#2e3220]/10 bg-[#F4F1EA] text-[#2e3220] hover:border-[#737a3d]/40"
                    }`}
                >
                    <p
                        className={`text-[10px] font-bold uppercase tracking-widest ${filter === "past" ? "text-[#F4F1EA]/80" : "text-[#737a3d]"}`}
                    >
                        Previously Rented
                    </p>
                    <p className="mt-1 font-display text-2xl sm:text-4xl font-bold">
                        {loading ? "…" : (data.past?.length || 0)}
                    </p>
                    <p
                        className={`mt-1 text-xs ${filter === "past" ? "text-[#F4F1EA]/85" : "text-[#5a6142]"}`}
                    >
                        Completed rentals
                    </p>
                </button>
            </section>

            {loading ? (
                <div
                    data-testid={MY_RENTALS.loading}
                    className="mt-10 space-y-4"
                >
                    {Array.from({ length: 2 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-32 animate-pulse rounded-3xl bg-[#E8E2D0]"
                        />
                    ))}
                </div>
            ) : !hasAny ? (
                <div
                    data-testid={MY_RENTALS.emptyState}
                    className="mt-10 rounded-3xl border border-dashed border-[#2e3220]/20 bg-[#F4F1EA] px-6 py-16 text-center"
                >
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                        <Receipt className="h-6 w-6" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-[#2e3220]">
                        No rentals yet
                    </h2>
                    <p className="mx-auto mt-2 max-w-sm text-sm text-[#5a6142]">
                        When you rent something on Lendly, it shows up here with the dates
                        and what you paid.
                    </p>
                    <Link
                        to="/browse"
                        data-testid={`${MY_RENTALS.browseBtn}-empty`}
                        className="btn-primary mt-6"
                    >
                        <PackageSearch className="h-4 w-4" />
                        Browse items nearby
                    </Link>
                </div>
            ) : (
                <>
                    {filter === "active" ? (
                        <Section
                            title="Currently borrowing"
                            bucket="active"
                            items={data.active}
                            testId={MY_RENTALS.sectionActive}
                            onReview={setReviewTarget}
                            onReturn={handleReturn}
                            onAsk={setAskTarget}
                            busyId={busyId}
                        />
                    ) : filter === "pending_payment" ? (
                        <Section
                            title="Pending Payment"
                            bucket="awaiting_payment"
                            items={awaiting}
                            testId="rentals-section-awaiting"
                            onReview={setReviewTarget}
                            onPay={handlePay}
                            onCancel={handleCancel}
                            onAsk={setAskTarget}
                            busyId={busyId}
                        />
                    ) : filter === "return_pending" ? (
                        <Section
                            title="Pending Return"
                            bucket="return_pending"
                            items={data.return_pending || []}
                            testId="rentals-section-return-pending"
                            onReview={setReviewTarget}
                            onAsk={setAskTarget}
                            busyId={busyId}
                        />
                    ) : filter === "past" ? (
                        <Section
                            title="Past rentals"
                            bucket="completed"
                            items={data.past}
                            testId={MY_RENTALS.sectionPast}
                            onReview={setReviewTarget}
                            onReturn={handleReturn}
                            onDispute={handleDispute}
                            busyId={busyId}
                        />
                    ) : (
                        <>
                            <Section
                                title="Ready to pay"
                                bucket="awaiting_payment"
                                items={awaiting}
                                testId="rentals-section-awaiting"
                                onReview={setReviewTarget}
                                onPay={handlePay}
                                onCancel={handleCancel}
                                onAsk={setAskTarget}
                                busyId={busyId}
                            />
                            <Section
                                title="Awaiting response"
                                bucket="pending"
                                items={pending}
                                testId="rentals-section-pending"
                                onReview={setReviewTarget}
                                onPay={handlePay}
                                onCancel={handleCancel}
                                onAsk={setAskTarget}
                                busyId={busyId}
                            />
                            <Section
                                title="Currently borrowing"
                                bucket="active"
                                items={data.active}
                                testId={MY_RENTALS.sectionActive}
                                onReview={setReviewTarget}
                                onReturn={handleReturn}
                                onAsk={setAskTarget}
                                busyId={busyId}
                            />
                            <Section
                                title="Return sent — waiting on lender"
                                bucket="return_pending"
                                items={data.return_pending || []}
                                testId="rentals-section-return-pending"
                                onReview={setReviewTarget}
                                onDispute={handleDispute}
                                onAsk={setAskTarget}
                                busyId={busyId}
                            />
                            <Section
                                title="Upcoming"
                                bucket="upcoming"
                                items={data.upcoming}
                                testId={MY_RENTALS.sectionUpcoming}
                                onReview={setReviewTarget}
                                onAsk={setAskTarget}
                            />
                            <Section
                                title="Past rentals"
                                bucket="completed"
                                items={data.past}
                                testId={MY_RENTALS.sectionPast}
                                onReview={setReviewTarget}
                                onReturn={handleReturn}
                                onDispute={handleDispute}
                                busyId={busyId}
                            />
                        </>
                    )}
                    {filter === "total" && (
                        <Section
                            title="Declined / cancelled"
                            bucket="declined"
                            items={data.declined}
                            testId="rentals-section-declined"
                            onReview={setReviewTarget}
                        />
                    )}
                </>
            )}

            <ReviewModal
                rental={reviewTarget}
                open={!!reviewTarget}
                onOpenChange={(o) => !o && setReviewTarget(null)}
                onSubmitted={handleReviewSubmitted}
            />

            <ReturnRentalModal
                rental={returnTarget}
                open={!!returnTarget}
                onOpenChange={(o) => !o && setReturnTarget(null)}
                onReturned={handleReturned}
            />

            <DisputeThreadModal
                rental={disputeTarget}
                currentUserId={user?.user_id}
                open={!!disputeTarget}
                onOpenChange={(o) => !o && setDisputeTarget(null)}
                onChanged={handleDisputeChanged}
            />

            <AskQuestionModal
                item={
                    askTarget
                        ? {
                              id: askTarget.item_id,
                              name: askTarget.item_name,
                              owner_name: askTarget.owner_name,
                          }
                        : null
                }
                open={!!askTarget}
                onOpenChange={(o) => !o && setAskTarget(null)}
            />
            <PayoutRequiredDialog
                open={!!payoutBlockedRental}
                onOpenChange={(o) => !o && setPayoutBlockedRental(null)}
                variant="renter"
                itemId={payoutBlockedRental?.item_id}
                itemName={payoutBlockedRental?.item_name}
                ownerName={payoutBlockedRental?.owner_name}
            />
            <NextStepDialog step={nextStep} onClose={() => setNextStep(null)} />
        </main>
    );
};

export default MyRentals;
